import { useMemo } from 'react';
import { useExperimentGroup } from 'shared/src/context/UserContext/useExperimentGroup';
import type { GoodsDetailContentKeys } from '../types/GoodsDetailContentKeys';
import { useGoodsBasic } from '../queries/useGoodsBasic';
import { useGoodsDetail } from '../queries/useGoodsDetail';
import { useGoodsDiscounts } from '../queries/useGoodsDiscounts';
import { useGoodsCoupon } from '../queries/useGoodsCoupon';
import { useGoodsReviewSummary } from '../queries/useGoodsReviewSummary';
import { useMumulLiveProfileStatus } from '../queries/useMumulLiveProfileStatus';
import { useCodiGoodsList } from '../components/GoodsCarousel/queries/useCodiGoodsList';
import { useMidRelatedRecommendGoodsList } from '../components/GoodsCarousel/queries/useMidRelatedRecommendGoodsList';
// import { useSameMarketRecommendOnly } from './useSameMarketRecommendOnly';
import { isAndroid } from 'shared/src/utils';

// 상단 코어 섹션 — pre-render(usePreRenderParams)로 basic/detail 도착 전에도 즉시 그릴 수 있어
// 항상 표시한다. 모듈 스코프라 not-ready 동안 reference/순서가 안정적이다.
const CONTENT_KEYS: GoodsDetailContentKeys[] = [
  'cover',
  'promotions',
  'market',
  'info',
  'expected-purchase-price',
  'coupons',
  'delivery-info',
  'brand-info',
  'mumul-live-profile',
  'review-summary',
  'carousel',
  'content-tab',
  'content',
];

/**
 * 상품상세 리스트에 노출할 섹션 key 목록.
 *
 * - iOS/web: 전체를 즉시 렌더(기존 동작 유지).
 * - Android: cover/promotions/market/info/content 는 항상 렌더하고, 나머지 섹션은
 *   각 섹션이 의존하는 API 응답이 도착(success)한 뒤에만 리스트에 편입한다.
 *   비어있는 섹션이 초기 레이아웃에 끼어 리플로우를 유발하는 것을 줄인다.
 *
 * 반환 배열은 항상 CONTENT_KEYS 의 원래 순서를 유지한다(append 순서 X).
 */
export const useGoodsDetailContentKeys = (): GoodsDetailContentKeys[] => {
  const { data: goodsBasic, isSuccess: basicReady } = useGoodsBasic();
  const { data: goodsDetail, isSuccess: detailReady } = useGoodsDetail();
  const { isSuccess: discountsReady } = useGoodsDiscounts();
  const { isSuccess: couponReady } = useGoodsCoupon();
  const { isSuccess: reviewReady } = useGoodsReviewSummary();
  const { isSuccess: codiReady } = useCodiGoodsList();
  const { isSuccess: midReady } = useMidRelatedRecommendGoodsList();
  // 기획전 이벤트 연관상품 off 실험: mid 쿼리를 disabled 하므로 carousel 준비 판정에서 제외.
  // const sameMarketRecommendOnly = useSameMarketRecommendOnly();

  // mumul-live-profile 는 실험 참여 대상(EXP1789 A/B) & 브랜드 없음일 때만 상태를 조회한다.
  // 대조군(A)도 노출 모수 집계용 디바이더를 렌더하므로 준비 판정에 포함한다.
  // (BrandInfo 가 1순위라 brand 가 있으면 무물라이브는 조회·노출 생략)
  // 로깅/뷰포트 트래킹이 있는 useMumulLiveProfile 대신, 부수효과 없는 상태 쿼리로만 준비 여부를 판정.
  const profileSno = goodsDetail?.goods.market.mumul_live_profile_sno ?? null;
  const isMumulExpTarget = useExperimentGroup({
    sno: 'EXP1789',
    targetGroups: ['A', 'B'],
  });
  const hasBrand = goodsBasic?.brand != null;
  const { isSuccess: mumulReady } = useMumulLiveProfileStatus(
    profileSno,
    isMumulExpTarget && !hasBrand,
  );

  return useMemo(() => {
    // Android 외(iOS/web)는 기존처럼 전체를 즉시 렌더.
    if (!isAndroid) return CONTENT_KEYS;

    const contentKeys =
      basicReady &&
      detailReady &&
      reviewReady &&
      codiReady &&
      midReady &&
      discountsReady;

    // 각 섹션이 의존하는 API 응답이 도착(success)해야 리스트에 편입.
    // 맵에 없는 코어 섹션(cover/promotions/market/info/content)은 항상 true.
    const readiness: Partial<Record<GoodsDetailContentKeys, boolean>> = {
      'expected-purchase-price': discountsReady,
      coupons: couponReady,
      'delivery-info': basicReady && discountsReady,
      'brand-info': basicReady,
      'mumul-live-profile': mumulReady,
      'review-summary': basicReady && discountsReady && reviewReady,
      carousel: true,
      'content-tab': contentKeys,
      content: contentKeys,
    };

    return CONTENT_KEYS.filter(key => readiness[key] ?? true);
  }, [
    basicReady,
    detailReady,
    discountsReady,
    couponReady,
    reviewReady,
    codiReady,
    midReady,
    mumulReady,
  ]);
};
